import { useState, useEffect } from "react";
import { Header } from "../components/layout/Header";
import { adminApi, type Plugin } from "../lib/api";
import { CheckIcon, XIcon, FileIcon, ExternalLinkIcon, ClockIcon } from "../components/ui/Icons";

export function AdminPage() {
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [selectedPlugin, setSelectedPlugin] = useState<Plugin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loadPlugins = async () => {
    try {
      const data = await adminApi.getPendingPlugins();
      setPlugins(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load plugins");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPlugins();
  }, []);

  const handleReview = async (status: "APPROVED" | "REJECTED") => {
    if (!selectedPlugin) return;

    setIsReviewing(true);
    setError(null);

    try {
      await adminApi.reviewPlugin(selectedPlugin.id, status, reviewNote || undefined);
      setSelectedPlugin(null);
      setReviewNote("");
      await loadPlugins();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to review plugin");
    } finally {
      setIsReviewing(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-base">
      <Header />

      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-20" />
      </div>

      <main className="relative pt-24">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold tracking-tight text-primary">
              Plugin Review Queue
            </h1>
            <p className="mt-2 text-muted">Review and approve submitted plugins</p>
          </div>

          {error && (
            <div className="mb-6 rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
              {error}
            </div>
          )}

          <div className="grid gap-8 lg:grid-cols-2">
            {/* Pending plugins list */}
            <div>
              <h2 className="mb-4 font-display text-lg font-bold text-secondary">
                Pending Plugins ({plugins.length})
              </h2>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                </div>
              ) : plugins.length === 0 ? (
                <div className="card-glow p-8 text-center">
                  <ClockIcon size={48} className="mx-auto mb-4 text-muted" />
                  <p className="text-muted">No plugins pending review</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {plugins.map((plugin) => (
                    <button
                      key={plugin.id}
                      onClick={() => {
                        setSelectedPlugin(plugin);
                        setReviewNote("");
                      }}
                      className={`card-glow w-full p-4 text-left transition-all ${
                        selectedPlugin?.id === plugin.id
                          ? "border-amber/50 ring-1 ring-amber/30"
                          : ""
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-display font-bold text-primary">{plugin.name}</h3>
                          <p className="mt-1 font-mono text-xs text-muted">
                            by {plugin.authorName || plugin.author.name}
                          </p>
                        </div>
                        <span className="rounded-full bg-warning/20 px-2 py-1 text-xs text-warning">
                          Pending
                        </span>
                      </div>
                      <p className="mt-2 line-clamp-2 text-sm text-muted">{plugin.description}</p>
                      <div className="mt-3 flex items-center gap-4 text-xs text-muted">
                        <span>{plugin.files.length} files</span>
                        <span>
                          {plugin.tags.length > 0 ? plugin.tags.slice(0, 2).join(", ") : "No tags"}
                        </span>
                        <span>Submitted {formatDate(plugin.submittedAt)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Review panel */}
            <div>
              <h2 className="mb-4 font-display text-lg font-bold text-secondary">Review Details</h2>

              {selectedPlugin ? (
                <div className="card-glow p-6">
                  {/* Plugin info */}
                  <div className="mb-6">
                    <h3 className="font-display text-xl font-bold text-primary">
                      {selectedPlugin.name}
                    </h3>
                    <p className="mt-1 font-mono text-sm text-muted">
                      {selectedPlugin.slug} • v{selectedPlugin.versions[0]?.version || "1.0.0"}
                    </p>
                  </div>

                  {/* Details */}
                  <dl className="mb-6 space-y-3">
                    <div className="flex justify-between border-b border-elevated pb-2">
                      <dt className="font-mono text-sm text-muted">Author</dt>
                      <dd className="font-mono text-sm text-secondary">
                        {selectedPlugin.authorName || selectedPlugin.author.name}
                      </dd>
                    </div>
                    <div className="flex justify-between border-b border-elevated pb-2">
                      <dt className="font-mono text-sm text-muted">Tags</dt>
                      <dd className="font-mono text-sm text-secondary">
                        {selectedPlugin.tags.length > 0 ? selectedPlugin.tags.join(", ") : "None"}
                      </dd>
                    </div>
                    <div className="flex justify-between border-b border-elevated pb-2">
                      <dt className="font-mono text-sm text-muted">Submitted</dt>
                      <dd className="font-mono text-sm text-secondary">
                        {formatDate(selectedPlugin.submittedAt)}
                      </dd>
                    </div>
                    {selectedPlugin.repositoryUrl && (
                      <div className="flex justify-between border-b border-elevated pb-2">
                        <dt className="font-mono text-sm text-muted">Repository</dt>
                        <dd>
                          <a
                            href={selectedPlugin.repositoryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 font-mono text-sm text-amber hover:text-amber/80"
                          >
                            View <ExternalLinkIcon size={12} />
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>

                  {/* Description */}
                  <div className="mb-6">
                    <h4 className="mb-2 font-mono text-sm text-muted">Description</h4>
                    <p className="text-sm text-secondary">{selectedPlugin.description}</p>
                  </div>

                  {/* Files */}
                  <div className="mb-6">
                    <h4 className="mb-2 font-mono text-sm text-muted">
                      Files ({selectedPlugin.files.length})
                    </h4>
                    <div className="space-y-2">
                      {selectedPlugin.files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between rounded-lg bg-surface/50 p-3"
                        >
                          <div className="flex items-center gap-3">
                            <FileIcon size={16} className="text-amber" />
                            <div>
                              <p className="font-mono text-sm text-secondary">{file.fileName}</p>
                              <p className="font-mono text-xs text-muted">
                                {file.fileType} • {(file.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <a
                            href={file.s3Url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded p-1 text-muted hover:bg-elevated hover:text-secondary"
                          >
                            <ExternalLinkIcon size={14} />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Review note */}
                  <div className="mb-6">
                    <label htmlFor="reviewNote" className="mb-2 block font-mono text-sm text-muted">
                      Review Note (optional)
                    </label>
                    <textarea
                      id="reviewNote"
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      placeholder="Add feedback for the author..."
                      rows={3}
                      className="input-field resize-none"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleReview("APPROVED")}
                      disabled={isReviewing}
                      className="btn-primary flex-1 bg-success from-success to-success hover:from-success/80 hover:to-success/90"
                    >
                      <CheckIcon size={18} />
                      {isReviewing ? "Processing..." : "Approve"}
                    </button>
                    <button
                      onClick={() => handleReview("REJECTED")}
                      disabled={isReviewing}
                      className="btn-secondary flex-1 border-danger/30 text-danger hover:border-danger/50 hover:bg-danger/10"
                    >
                      <XIcon size={18} />
                      Reject
                    </button>
                  </div>
                </div>
              ) : (
                <div className="card-glow flex flex-col items-center justify-center p-12 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-surface">
                    <FileIcon size={24} className="text-muted" />
                  </div>
                  <p className="text-muted">Select a plugin to review</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
